import React, { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import ErrorBoundary from '../shared/ErrorBoundary';
import { palette, withOpacity } from '../../../../utils/designTokens';
import { SECURITY_NO_ISSUES } from '../../../../utils/strings';
import { getDetectionLabel } from '../../../../utils/signalUtils';

const TAG_LABELS = {
  safety: 'Safety',
  agentic: 'Agentic',
  quality: 'Quality',
  rag: 'RAG',
  conversational: 'Conversational',
  untagged: 'Untagged',
};

const POLARITY_ORDER = ['negative', 'positive', 'neutral'];

const POLARITY_STYLES = {
  negative: {
    color: palette.status.danger,
    bg: withOpacity(palette.status.danger, 6),
    border: withOpacity(palette.status.danger, 22),
    leftBorder: palette.status.danger,
    shadow: `0 0 0 1px ${withOpacity(palette.status.danger, 18)}, 0 8px 18px ${withOpacity(palette.status.danger, 8)}`,
  },
  positive: {
    color: palette.status.success,
    bg: withOpacity(palette.status.success, 8),
    border: withOpacity(palette.status.success, 22),
    leftBorder: palette.status.success,
    shadow: `0 0 0 1px ${withOpacity(palette.status.success, 16)}, 0 8px 18px ${withOpacity(palette.status.success, 7)}`,
  },
  neutral: {
    color: palette.text.muted,
    bg: palette.neutrals.surfaceSubtle,
    border: palette.borders.subtle,
    leftBorder: palette.text.muted,
    shadow: `0 0 0 1px ${withOpacity(palette.text.muted, 18)}`,
  },
};

const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return tags
    .filter(tag => typeof tag === 'string' && tag.trim())
    .map(tag => tag.trim().toLowerCase());
};

const normalizePolarity = (polarity) => {
  if (polarity === 'positive') return 'positive';
  if (polarity === 'negative') return 'negative';
  return 'neutral';
};

const formatConfidence = (confidence) => {
  if (typeof confidence !== 'number') return 'N/A';
  return `${(confidence * 100).toFixed(0)}%`;
};

const EXCLUDED_DETAIL_KEYS = new Set(['model', 'threshold', 'evidence']);

const getCompactDetails = (details) => {
  if (!details || Array.isArray(details) || typeof details !== 'object') return [];

  return Object.entries(details)
    .filter(([key]) => !EXCLUDED_DETAIL_KEYS.has(key))
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .filter(([, value]) => typeof value !== 'string' || value.length <= 80)
    .slice(0, 3);
};

const formatTagLabel = (tag) => TAG_LABELS[tag] || tag.replace(/_/g, ' ');

const formatSubcategory = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const evidenceDisplayItems = (evidence) => {
  if (!Array.isArray(evidence)) return [];
  return evidence
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      label: item.summary || item.field || 'Evidence',
      preview: item.text_preview || '',
    }));
};

const evidenceText = (detection) => {
  if (typeof detection.evidence === 'string' && detection.evidence.trim()) {
    return detection.evidence;
  }
  if (typeof detection.details?.evidence === 'string' && detection.details.evidence.trim()) {
    return detection.details.evidence;
  }
  return '';
};

/**
 * SecurityTab - Displays classifier detections for a span.
 * Compact list model: all detections are shown together, cards remain polarity-colored.
 */
const SecurityTab = ({ span }) => {
  const detections = useMemo(
    () => (Array.isArray(span?.detections) ? span.detections : []),
    [span]
  );

  const all = useMemo(
    () =>
      detections.map((detection, idx) => ({
        ...detection,
        _idx: idx,
        _tags: normalizeTags(detection.tags),
        _polarity: normalizePolarity(detection.polarity),
      })),
    [detections]
  );

  if (!span) {
    throw new Error('SecurityTab requires span prop');
  }

  if (detections.length === 0) {
    return (
      <div className="text-sm text-text-muted">
        {SECURITY_NO_ISSUES}
      </div>
    );
  }

  const sortedSelected = [...all].sort((a, b) =>
    POLARITY_ORDER.indexOf(a._polarity) - POLARITY_ORDER.indexOf(b._polarity)
  );

  return (
    <ErrorBoundary>
      <div>
        {/* Compact cards */}
        <div className="space-y-2">
          {sortedSelected.map((detection, index) => {
            const tone = POLARITY_STYLES[detection._polarity];
            const details = getCompactDetails(detection.details);
            const tags = detection._tags || [];
            const structuredEvidence = evidenceDisplayItems(detection.details?.evidence);
            const textEvidence = structuredEvidence.length > 0 ? '' : evidenceText(detection);
            const label = getDetectionLabel(detection) || 'Unknown Signal';
            const subcategory = formatSubcategory(detection.primary_subtype);
            const showSubcategory = !!subcategory && subcategory.toLowerCase() !== label.toLowerCase();
            const eventKey = formatSubcategory(detection.primary_event_key);

            return (
              <div
                key={`${detection.classifier_key || 'unknown'}-${detection._idx ?? index}`}
                className="relative overflow-hidden rounded border p-2.5 pl-4"
                style={{
                  backgroundColor: tone.bg,
                  borderColor: tone.border,
                  boxShadow: tone.shadow,
                }}
              >
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-0 h-full w-1.5"
                  style={{ backgroundColor: tone.leftBorder }}
                />
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="min-w-0 flex items-start flex-wrap gap-1.5">
                    <span className="text-sm font-semibold break-words" style={{ color: palette.text.primary }}>
                      {label}
                    </span>
                    {tags.map(tag => (
                      <span
                        key={tag}
                        className="px-1.5 py-0.5 rounded text-[10px]"
                        style={{
                          backgroundColor: palette.neutrals.surfaceMid,
                          color: palette.text.supporting,
                        }}
                      >
                        {formatTagLabel(tag)}
                      </span>
                    ))}
                    {showSubcategory && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px]"
                        style={{
                          backgroundColor: palette.neutrals.surfaceMid,
                          color: palette.text.muted,
                        }}
                      >
                        {subcategory}
                      </span>
                    )}
                    {!!eventKey
                      && eventKey.toLowerCase() !== label.toLowerCase()
                      && eventKey.toLowerCase() !== subcategory.toLowerCase()
                      && (
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px]"
                        style={{
                          backgroundColor: palette.neutrals.surfaceMid,
                          color: palette.text.muted,
                        }}
                      >
                        {eventKey}
                      </span>
                    )}
                    {!!detection.description && (
                      <span title={detection.description} style={{ color: palette.text.muted }}>
                        <AlertCircle size={13} />
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-mono" style={{ color: tone.color }}>
                    {formatConfidence(detection.confidence)}
                  </span>
                </div>

                {!!detection.reason && (
                  <div
                    className="text-xs mb-1"
                    style={{ color: palette.text.supporting }}
                  >
                    {detection.reason}
                  </div>
                )}

                {!!textEvidence && (
                  <div
                    className="text-xs mt-1"
                    style={{ color: palette.text.supporting }}
                  >
                    <span style={{ color: palette.text.muted }}>Evidence: </span>
                    {textEvidence}
                  </div>
                )}

                {structuredEvidence.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {structuredEvidence.slice(0, 2).map((item, idx) => (
                      <div key={`${idx}:${item.label}`} className="text-xs" style={{ color: palette.text.supporting }}>
                        <span style={{ color: palette.text.muted }}>Evidence: </span>
                        {item.label}
                        {item.preview && (
                          <div className="mt-0.5 font-mono text-[10px] leading-relaxed" style={{ color: palette.text.muted, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {item.preview}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {details.length > 0 && (
                  <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
                    {details.map(([key, value]) => (
                      <span key={key} className="text-[10px]" style={{ color: palette.text.muted }}>
                        {key}: {typeof value === 'number' ? value.toFixed(3).replace(/\.000$/, '') : String(value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SecurityTab;
